import { Button, Form, Input } from 'antd';
import { LoadingSpinnerComponent } from 'components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tagApiService } from 'service/api/tagApiService';
import { TagInterface } from 'type/tagType';
import { playlistRuleHandlers } from '../handlers/playlistRuleHandler';
import { playlistRuleService } from '../services/playlistRuleService';
import { ContentRule } from './ContentRule';
import { SeasonRule } from './SeasonRule';
import { SeriesRule } from './SeriesRule';
import { TagRule } from './TagRule';

export const PlaylistRule: React.FC<{
  /** Set for a bebridge (work order) playlist — adds the feed's own caveats to the rules. */
  isWorkOrder?: boolean;
}> = ({ isWorkOrder }) => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const [tags, setTags] = useState<TagInterface[]>([]);
  const { handleSave } = playlistRuleHandlers({
    form,
    playlistId: Number(id),
  });
  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const fetchedRules = await playlistRuleService.fetchRules(Number(id));
      const fetchedTags = await tagApiService.fetchAllTags();
      setTags(fetchedTags);
      setIsLoading(false);
      fetchedRules.forEach(rule => {
        // `ids` is the backend's older spelling of the `content` rule — both are read by
        // extract_rule_params, so map it onto the same field or the picker looks empty.
        const fieldName = rule.value_type === 'ids' ? 'content' : rule.value_type;
        form.setFieldValue(
          fieldName,
          rule.value.split(',').map((value: string) => Number(value))
        );
      });
    };
    fetchData();
  }, [id]);

  return (
    <>
      {isLoading ? (
        <LoadingSpinnerComponent />
      ) : (
        <Form form={form}>
          <h3 style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <span>Recent</span>
            <span
              style={{
                flex: 1,
                borderBottom: '1px solid #666',
                marginLeft: '8px',
                height: '0.5em',
              }}
            />
          </h3>
          <div
            style={{
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px', // Gap between label and input
            }}
          >
            <Form.Item name="recent" noStyle>
              <Input style={{ width: '100px' }} />
            </Form.Item>
            <span style={{ fontSize: '16px' }}> days</span>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <TagRule tags={tags} form={form} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <SeriesRule form={form} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <SeasonRule form={form} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <ContentRule form={form} isWorkOrder={isWorkOrder} />
          </div>
          <Form.Item style={{ marginTop: 16, textAlign: 'center' }}>
            {/* The save must finish before navigating. Leaving earlier unmounts the Form, and
                since React flushes a click's state updates before the awaited
                validateFields() resumes, the rules were being read from a torn-down form —
                an empty payload that the server accepted as "nothing to do". */}
            <Button
              type="primary"
              loading={isSaving}
              onClick={async () => {
                setIsSaving(true);
                const saved = await handleSave();
                setIsSaving(false);
                if (saved) {
                  navigate('/playlists');
                }
              }}
            >
              save
            </Button>
          </Form.Item>
        </Form>
      )}
    </>
  );
};

export default PlaylistRule;
